import { useEffect } from "react";
import api from "./api";

const SESSION_KEY = "session_uuid";
const HEARTBEAT_INTERVAL_MS = 60 * 1000;

let heartbeatIntervalId = null;
let currentPage = null;
let titleObserver = null;
let lastKnownTitle = null;

const ensureTitleObserver = () => {
  if (titleObserver) return;
  if (typeof MutationObserver === "undefined" || !document?.head) return;
  lastKnownTitle = document.title;
  titleObserver = new MutationObserver(() => {
    if (document.title !== lastKnownTitle) {
      lastKnownTitle = document.title;
      if (currentPage) currentPage.titleChanged = true;
    }
  });
  titleObserver.observe(document.head, {
    childList: true,
    subtree: true,
    characterData: true,
  });
};

const resolvePageName = (page) => {
  if (!page) return null;
  if (page.titleChanged) return document.title || null;
  return null;
};

export const setSessionUuid = (uuid) => {
  if (!uuid) return;
  localStorage.setItem(SESSION_KEY, uuid);
  startHeartbeat();
};

export const getSessionUuid = () => localStorage.getItem(SESSION_KEY);

export const clearSessionUuid = () => {
  localStorage.removeItem(SESSION_KEY);
  stopHeartbeat();
  currentPage = null;
};

export const startHeartbeat = () => {
  stopHeartbeat();
  const uuid = getSessionUuid();
  if (!uuid) return;
  heartbeatIntervalId = window.setInterval(() => {
    const active = getSessionUuid();
    if (!active) {
      stopHeartbeat();
      return;
    }
    api.trackingHeartbeat(active).catch(() => {});
  }, HEARTBEAT_INTERVAL_MS);
};

export const stopHeartbeat = () => {
  if (heartbeatIntervalId) {
    window.clearInterval(heartbeatIntervalId);
    heartbeatIntervalId = null;
  }
};

export const endSession = () => {
  const uuid = getSessionUuid();
  if (!uuid) return Promise.resolve();
  return api.trackingEndSession(uuid).catch(() => {});
};

export const trackPageChange = (location) => {
  const uuid = getSessionUuid();
  if (!uuid) return;

  ensureTitleObserver();

  const now = new Date().toISOString();
  const url = window.location.origin + location.pathname + location.search;
  const isFirst = !currentPage;

  if (currentPage) {
    api
      .trackingLogPage({
        session_uuid: uuid,
        url: currentPage.url,
        page_name: resolvePageName(currentPage),
        entered_at: currentPage.entered_at,
        left_at: now,
      })
      .catch(() => {});
  }

  currentPage = {
    url,
    entered_at: now,
    titleChanged: isFirst,
  };
};

export const flushCurrentPage = () => {
  const uuid = getSessionUuid();
  if (!uuid || !currentPage) return;

  const now = new Date().toISOString();
  const payload = {
    session_uuid: uuid,
    url: currentPage.url,
    page_name: resolvePageName(currentPage),
    entered_at: currentPage.entered_at,
    left_at: now,
  };

  try {
    const blob = new Blob([JSON.stringify(payload)], { type: "application/json" });
    const token = localStorage.getItem("access_token");
    const base = process.env.REACT_APP_API_URL || "";
    if (navigator.sendBeacon && !token) {
      navigator.sendBeacon(`${base}/tracking/page`, blob);
    } else {
      api.trackingLogPage(payload).catch(() => {});
    }
  } catch (e) {}

  currentPage = null;
};

export const trackResource = ({ resource_type, resource_id, resource_name }) => {
  const uuid = getSessionUuid();
  if (!uuid) return Promise.resolve();
  return api
    .trackingLogResource({
      session_uuid: uuid,
      resource_type,
      resource_id,
      resource_name,
      accessed_at: new Date().toISOString(),
    })
    .catch(() => {});
};

const patchResourceKeepalive = (id, leftAt) => {
  try {
    const token = localStorage.getItem("access_token");
    const base = process.env.REACT_APP_API_URL || "";
    fetch(`${base}/tracking/resource/${id}`, {
      method: "PATCH",
      keepalive: true,
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({ left_at: leftAt }),
    }).catch(() => {});
  } catch (e) {}
};

export const TrackResource = (props) => {
  useTrackResource(props);
  return null;
};

export const useTrackResource = ({
  resource_type,
  resource_id,
  resource_name,
  enabled = true,
} = {}) => {
  useEffect(() => {
    if (!enabled || !resource_type) return undefined;
    const uuid = getSessionUuid();
    if (!uuid) return undefined;

    const state = { recordId: null, pendingCloseAt: null, closed: false };
    const accessedAt = new Date().toISOString();

    api
      .trackingLogResource({
        session_uuid: uuid,
        resource_type,
        resource_id: resource_id ?? null,
        resource_name: resource_name ?? null,
        accessed_at: accessedAt,
      })
      .then((res) => {
        state.recordId = res?.data?.id ?? null;
        if (state.pendingCloseAt && state.recordId && !state.closed) {
          state.closed = true;
          api
            .trackingUpdateResource(state.recordId, {
              left_at: state.pendingCloseAt,
            })
            .catch(() => {});
        }
      })
      .catch(() => {});

    const closeBeacon = () => {
      if (state.closed || !state.recordId) return;
      state.closed = true;
      patchResourceKeepalive(state.recordId, new Date().toISOString());
    };

    window.addEventListener("beforeunload", closeBeacon);

    return () => {
      window.removeEventListener("beforeunload", closeBeacon);
      if (state.closed) return;
      const leftAt = new Date().toISOString();
      if (!state.recordId) {
        state.pendingCloseAt = leftAt;
        return;
      }
      state.closed = true;
      api
        .trackingUpdateResource(state.recordId, { left_at: leftAt })
        .catch(() => {});
    };
  }, [enabled, resource_type, resource_id]);
};
